import * as React from 'react';

import DocumentCreationButton from 'Components/DocumentCreationButton';
import DocumentHeader from '../../../components/DocumentHeader';
import DocumentWrapper from '../../../components/DocumentWrapper';
import FormEditingHandler from '../../../containers/FormEditingHandler';

import TDCSForm from './Form';
import TDCSHistory from './HistoryRowData';
import { useIntl } from 'react-intl';
import { sortPartialDate } from 'neuro-utils';
import DoctorsOrdersReview from './Form/Sessions/components/DoctorsOrdersReview';
import { useAppSelector } from 'Store/index';

export const TDCSContext = React.createContext<ITDCSContext>({
  fm: (m: string) => m,
  isEditing: false,
  formData: {
    document: {
      _id: 'temp-id',
      _cid: 'temp-cid',
      _type: 'temp-type',
      _cdate: 0,
      _editing: false,
      _docCreateDate: 0,
      _creator: 'testUser',
      _ownerOrg: 'stellarq',
      _lockedFor: null,
      _source: null,
      _pastOwners: {},
    },
    onChange: () => {
      return;
    },
  },
  startEdit: (d: { _id?: string }) => (e: React.MouseEvent<Element, MouseEvent>) => {
    !!d;
    !!e;
    return;
  },
  documents: [],
  doctorsOrders: [],
  otherDoctorsOrders: [],
  ninmtTreatmentPeriods: [],
  contraIndications: [],
  symptomsAndLocations: [],
  doctorsOrdersReview: { active: false, completed: false },
  setDoctorsOrdersReview: () => '',
  anchor: undefined,
  setAnchor: () => '',
  /** For treatment efficiency functionalities */
  patientMySQStatus: undefined,
  /** For treatment efficiency functionalities */
  taskLists: [],
  /** For treatment efficiency functionalities */
  sortedAndMergedMyDocuments: [],
});

interface ITDCSContext {
  fm: (m: string) => string;
  isEditing: boolean;
  formData: IFormData<ITDCS>;
  documents: Array<ITDCS>;
  doctorsOrders: Array<IDoctorsOrder>;
  otherDoctorsOrders: Array<IDoctorsOrder>;
  ninmtTreatmentPeriods: Array<ININMTTreatmentPeriod>;
  contraIndications: Array<IContraIndicationToTreatment>;
  symptomsAndLocations: Array<ISymptomsAndLocations>;
  doctorsOrdersReview: { active: boolean; completed: boolean };
  setDoctorsOrdersReview: React.Dispatch<
    React.SetStateAction<{
      active: boolean;
      completed: boolean;
    }>
  >;
  anchor: string | undefined;
  setAnchor: React.Dispatch<React.SetStateAction<string | undefined>>;
  startEdit: (document: { _id?: string }) => (e: React.MouseEvent<Element, MouseEvent>) => void;
  patientMySQStatus: string | null | undefined;
  taskLists: ITaskList[];
  sortedAndMergedMyDocuments: ({ [key: string]: any } & IControlProps)[];
}

const TDCS = ({ documents }: IOwnProps): React.JSX.Element => {
  const patientMySQStatus = useAppSelector((s: IState) => s.myapp.mysqUserId);
  const taskLists = useAppSelector((s: IState) => s.myapp.taskLists);
  const sortedAndMergedMyDocs = useAppSelector((s: IState) => s.myapp.sortedAndMergedDocuments) || [];

  const [doctorsOrdersReview, setDoctorsOrdersReview] = React.useState<{ active: boolean; completed: boolean }>({
    active: false,
    completed: false,
  });

  // Anchor for scrolling to specific sections in form
  const [anchor, setAnchor] = React.useState<string | undefined>(undefined);

  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });
  const ninmtTreatmentPeriodDocs: Array<ININMTTreatmentPeriod> = documents.filter(
    d => d._type === 'ninmtTreatmentPeriod',
  );

  let doctorsOrdersDocs: Array<IDoctorsOrder> = [];
  let otherDoctorsOrdersDocs: Array<IDoctorsOrder> = [];
  let contraIndicationsDocs: Array<IContraIndicationToTreatment> = [];
  let symptomsAndLocationsDocs: Array<ISymptomsAndLocations> = [];
  let latestOrderWithTDCS: IDoctorsOrder;

  if (Array.isArray(ninmtTreatmentPeriodDocs) && ninmtTreatmentPeriodDocs.length > 0) {
    const latestTreatmentPeriodDate = ninmtTreatmentPeriodDocs.sort((n1, n2) => sortPartialDate(n2.date, n1.date))[0]
      .date;

    doctorsOrdersDocs = documents.filter(
      d =>
        d._type === 'doctorsOrders' &&
        // Only doctor's orders with TDCS are relevant
        Array.isArray((d as IDoctorsOrder).treatmentType) &&
        (d as IDoctorsOrder).treatmentType?.includes('tdcs') &&
        sortPartialDate(d.date, latestTreatmentPeriodDate) >= 0,
    );
    latestOrderWithTDCS = [...doctorsOrdersDocs].sort((d1, d2) => sortPartialDate(d2.date, d1.date))[0];

    otherDoctorsOrdersDocs = documents.filter(
      (d: IDoctorsOrder) =>
        d._type === 'doctorsOrders' &&
        sortPartialDate(d.date, latestTreatmentPeriodDate) >= 0 &&
        (!Array.isArray(d.treatmentType) || d.treatmentType?.length === 0) &&
        Object.keys(d).some(k => ['nextInterview', 'ninmtMeeting', 'requestFollowUp'].includes(k)),
    );

    contraIndicationsDocs = documents.filter(
      d => d._type === 'contraIndicationsToTreatment' && sortPartialDate(d.date, latestTreatmentPeriodDate) >= 0,
    ) as IContraIndicationToTreatment[];

    symptomsAndLocationsDocs = documents.filter(
      d =>
        d._type === 'symptomsAndLocations' &&
        sortPartialDate((d as ISymptomsAndLocations).startDate, latestTreatmentPeriodDate) >= 0,
    ) as ISymptomsAndLocations[];
  }

  const parsedDocs = JSON.parse(JSON.stringify(documents));
  const tdcsDocs: Array<ITDCS> = parsedDocs
    .filter((d: ITDCS) => d._type === 'tdcs')
    .map((d: ITDCS) => {
      if (Array.isArray(d.sessions) && d.sessions.length > 0) {
        d.sessions = d.sessions?.filter(s => !(s.removeTS || s.removeInfo));
      }
      return d;
    });

  // Track editing status of documents
  const isEditing = tdcsDocs.some(d => d._editing);

  // If not editing any docs, reset doctorsOrdersReview status if active
  React.useEffect(() => {
    if (!isEditing && doctorsOrdersReview.active) {
      setDoctorsOrdersReview({ active: false, completed: false });
    }
  }, [isEditing]);

  // Scroll to top when starting review of doctor's orders
  React.useEffect(() => {
    if (doctorsOrdersReview.active) {
      scrollTo(0, 0);
    } else {
      // Reset anchor when review is done
      setTimeout(() => setAnchor(undefined), 600);
    }
  }, [doctorsOrdersReview.active]);

  return (
    <FormEditingHandler
      name="tdcs"
      documents={tdcsDocs}
      toolbarProps={{
        saveButtonDisabled: doctorsOrdersReview.active,
        cancelButtonDisabled: doctorsOrdersReview.active,
      }}
    >
      {(editing, startEdit, formData, view): React.JSX.Element => (
        <TDCSContext.Provider
          value={{
            fm: fm,
            isEditing: !!editing && !view?.viewing,
            formData: formData,
            documents: tdcsDocs,
            doctorsOrders: doctorsOrdersDocs,
            otherDoctorsOrders: otherDoctorsOrdersDocs,
            ninmtTreatmentPeriods: ninmtTreatmentPeriodDocs,
            contraIndications: contraIndicationsDocs,
            symptomsAndLocations: symptomsAndLocationsDocs,
            doctorsOrdersReview: doctorsOrdersReview,
            setDoctorsOrdersReview: setDoctorsOrdersReview,
            anchor: anchor,
            setAnchor: setAnchor,
            startEdit: startEdit,
            patientMySQStatus: patientMySQStatus,
            taskLists: taskLists,
            sortedAndMergedMyDocuments: sortedAndMergedMyDocs,
          }}
        >
          <DocumentWrapper>
            <DocumentHeader
              name="tdcs"
              headerId={doctorsOrdersReview.active ? 'tdcs.doctorsOrdersReview' : 'tdcs.title'}
              headerWidth={9}
              infoText={doctorsOrdersReview.active ? 'tdcs.uncompletedDoctorsOrderMessage' : undefined}
              editing={editing}
              editButtons={
                <DocumentCreationButton
                  name="tdcs"
                  text="tdcs.newTdcs"
                  onClick={(e: React.MouseEvent): void => {
                    startEdit({}, 'tdcs')(e);
                  }}
                  disabled={!!(tdcsDocs.some(d => !d.hasEnded) || latestOrderWithTDCS?.tdcs?.endTDCS)}
                  disabledTooltip={
                    latestOrderWithTDCS?.tdcs?.endTDCS ? (
                      <span>{fm('tdcs.unableToAddNew')}</span>
                    ) : (
                      <span>{fm('tdcs.endOngoingToAddNew')}</span>
                    )
                  }
                />
              }
            />
            {editing ? (
              <React.Fragment>
                <div style={{ display: doctorsOrdersReview.active ? 'none' : undefined }}>
                  <TDCSForm />
                </div>
                <div style={{ display: doctorsOrdersReview.active ? undefined : 'none' }}>
                  <DoctorsOrdersReview />
                </div>
              </React.Fragment>
            ) : null}
            {!editing && !view?.viewing ? <TDCSHistory /> : null}
          </DocumentWrapper>
        </TDCSContext.Provider>
      )}
    </FormEditingHandler>
  );
};

interface IOwnProps {
  documents: Array<ITDCS>;
}

export default TDCS;
